<template>
    <div>
        <video
            v-if="$store.getters.getSecureAssessment && ($store.getters.getCapturePhotos || $store.getters.getAI)"
            class="question__camera"
            ref="cameraPreview"
            autoplay
            disablePictureInPicture
            muted
            playsinline
        ></video>
        <template v-for="(block, index) in assessment.blocks">
            <transition
                v-if="index == question"
                :key="index"
                name="fade"
                mode="out-in"
            >
                <div class="questionBlock">
                    <ScenarioBlock :scenario="block.scenario" :textDir="textDir" v-if="block.scenario" />
                    <component
                        :is="block.type + 'Block'"
                        :block="block"
                        :key="index"
                        :textDir="textDir"
                        @answerSelected="answerSelected"
                    />
                </div>
            </transition>
        </template>
        <div
            class="question__selector shadow"
            :class="{ show: showNav }"
        >
            <button
                class="question__selector__close"
                @click="$emit('toggleNav', false)"
            >
                <svg-icon
                    icon="close"
                    width="18"
                    height="18"
                />
            </button>
            <div class="question__selector__inner">
                <h2>{{ $t('questions') }}</h2>
                <p>{{ $t('assessmentNavigationPC') }}</p>
                <template v-if="skipped">
                    <h6>{{ $t('notAnswered') }}</h6>
                    <ul class="question__selector__list incomplete">
                        <template v-for="(block, index) in assessment.blocks">
                            <li
                                v-if="index <= lastQuestion && isUnanswered(block)"
                                :key="'q' + index"
                                @click="$emit('goToQuestion', index)"
                                class="question__selector__item"
                            >
                                <div>
                                    <span>{{ index + 1 }}</span>
                                </div>
                                <p>{{ textOnly(block.data.question) }}</p>
                            </li>
                        </template>
                    </ul>
                </template>
                <template v-if="answered">
                    <h6>{{ $t('Answered') }}</h6>
                    <ul class="question__selector__list completed">
                        <template v-for="(block, index) in assessment.blocks">
                            <li
                                v-if="index <= lastQuestion && isAnswered(block)"
                                :key="'q' + index"
                                @click="$emit('goToQuestion', index)"
                                class="question__selector__item"
                            >
                                <div>
                                    <span>{{ index + 1 }}</span>
                                </div>
                                <p>{{ textOnly(block.data.question) }}</p>
                            </li>
                        </template>
                    </ul>
                </template>
            </div>
        </div>
        <div class="fixed-bottom shadow bg-white">
            <div class="container mid2">
                <AssessmentTimer
                    :time="time"
                    :startTime="startTime"
                    :questionNum="question + 1"
                    :totalQuestions="assessment.blocks.length"
                />
                <div class="question__buttons d-flex">
                    <b-button
                        v-if="question !== 0"
                        variant="primary"
                        class="mr-2 p-3"
                        @click="$emit('goBack')"
                        >{{ $t('previousQuestion') }}</b-button
                    >
                    <b-button
                        v-if="question !== assessment.blocks.length - 1"
                        class="p-3"
                        variant="primary"
                        @click="$emit('goForward')"
                        >{{ nextText }}</b-button
                    >
                    <b-button
                        v-if="seenAll"
                        class="ml-2 p-3"
                        variant="primary"
                        @click="submit()"
                        >{{ $t('submit') }}</b-button
                    >
                </div>
            </div>
            <button
                class="question__menuBtn"
                @click="$emit('toggleNav')"
                v-b-tooltip.topleft
                :title="$t('questionBank')"
            >
                <svg-icon icon="menu" />
            </button>
        </div>
    </div>
</template>
<script>
import QuizBlock from '../blocks/QuizBlock';
import FreeTextBlock from '../blocks/FreeTextBlock';
import ScenarioBlock from '../blocks/Scenario.vue';
import SvgIcon from '@/components/SvgIcon';
import AssessmentTimer from './AssessmentTimer';
import Bugsnag from '@bugsnag/js';

export default {
    name: 'AssessmentExam',
    props: {
        assessment: Object,
        textDir: {
            type: String,
            default: '',
        },
        time: {
            type: Number,
            default: null,
        },
        startTime: {
            type: Object,
            required: true,
        },
        question: {
            type: Number,
            required: true,
        },
        lastQuestion: {
            type: Number,
            required: true,
        },
        seenAll: {
            type: Boolean,
            required: true,
        },
        showNav: {
            type: Boolean,
            required: true,
        },
        cameraStream: MediaStream,
    },
    components: {
        QuizBlock,
        FreeTextBlock,
        SvgIcon,
        AssessmentTimer,
        ScenarioBlock,
    },
    watch: {
        cameraStream: {
            immediate: true,
            handler(newStream) {
                this.bindCameraPreview(newStream);
            },
        },
    },
    computed: {
        currentBlock() {
            if (!this.assessment || !Array.isArray(this.assessment.blocks)) {
                return null;
            }
            return this.assessment.blocks[this.question] || null;
        },
        unansweredQuestionCount() {
            if (!this.assessment || !Array.isArray(this.assessment.blocks)) {
                return 0;
            }

            return this.assessment.blocks.filter((block) => this.isUnanswered(block)).length;
        },
        nextText() {
            if (this.isAnswered(this.currentBlock)) {
                return this.$t('nextQuestion');
            } else {
                return this.$t('skipQuestion');
            }
        },
        skipped() {
            for (let index = 0; index <= this.lastQuestion; index++) {
                if (this.isUnanswered(this.getBlock(index))) {
                    return true;
                }
            }
            return false;
        },
        answered() {
            for (let index = 0; index <= this.lastQuestion; index++) {
                if (this.isAnswered(this.getBlock(index))) {
                    return true;
                }
            }
            return false;
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.bindCameraPreview(this.cameraStream);
            if (this.$store.getters.getAI && this.$store.getters.getSecureAssessment && this.$refs.cameraPreview) {
                try {
                    this.$emit('startAI', this.$refs.cameraPreview);
                } catch (error) {
                    this.$emit('cameraFail');
                    Bugsnag.notify(error);
                }
            }
        });
        window.addEventListener('contextmenu', this.disableRightClick);
        if (this.time) {
            this.timeout = setTimeout(() => {
                this.$emit('proceed', 'outro', true);
            }, this.time);
        }
    },
    beforeDestroy() {
        window.removeEventListener('contextmenu', this.disableRightClick);
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    },
    methods: {
        getBlock(index) {
            if (!this.assessment || !Array.isArray(this.assessment.blocks)) {
                return null;
            }
            return this.assessment.blocks[index] || null;
        },
        isAnswered(block) {
            if (!block) {
                return false;
            }

            if (block.type === 'quiz') {
                return Array.isArray(block.response) ? block.response.length > 0 : !!block.response;
            }

            if (block.type === 'free-text') {
                return typeof block.response === 'string' ? block.response !== '' : !!block.response;
            }

            return !!block.response;
        },
        isUnanswered(block) {
            return !this.isAnswered(block);
        },
        bindCameraPreview(stream) {
            this.$nextTick(() => {
                if (this.$refs.cameraPreview) {
                    this.$refs.cameraPreview.srcObject = stream || null;
                }
            });
        },
        answerSelected(answer) {
            this.$emit('response', this.question, answer);
        },
        disableRightClick(e) {
            e.preventDefault();
        },
        submit() {
            const hasUnansweredQuestions = this.unansweredQuestionCount > 0;
            const messageKey = hasUnansweredQuestions
                ? 'assessmentSubmitIncompleteText'
                : 'assessmentSubmitConfirmText';
            const titleKey = hasUnansweredQuestions
                ? 'assessmentSubmitIncompleteTitle'
                : 'submit';

            this.$bvModal
                .msgBoxConfirm(this.$t(messageKey), {
                    title: this.$t(titleKey),
                    okTitle: this.$t('submit'),
                    cancelTitle: this.$t('cancel'),
                    centered: true,
                })
                .then((value) => {
                    if (value) {
                        this.$emit('submit');
                    }
                });
        },
        textOnly(input) {
            return input.replaceAll(/<[^>]*>/g, '');
        },
    },
};
</script>
